import { comparePositions, type NodeRole, type NodeStatus } from "@mapcraft-tasks/domain";
import { useEffect, useState } from "react";
import { trpc } from "./trpc";

export type ArchiveRow = {
	id: string;
	parentId: string | null;
	position: string;
	role: NodeRole;
	title: string;
	status: NodeStatus | null;
	archivedAt: Date | null;
	archivedOp: string | null;
};

export type ArchiveTreeNode = { row: ArchiveRow; children: ArchiveTreeNode[] };

/**
 * Единственная выборка, которая ходит в сеть напрямую, а не через стор.
 *
 * Так и задумано: архив в реплику не входит (offline-sync §5) — он холодный, редкий
 * и растёт неограниченно. Оффлайн эта линза честно показывает «нет связи», а не пустой
 * список: пустой список означал бы «архив пуст», и это была бы ложь.
 */
export function useArchive(): ArchiveRow[] | null {
	const [rows, setRows] = useState<ArchiveRow[] | null | undefined>(undefined);

	useEffect(() => {
		let alive = true;
		trpc.views.archive
			.query({})
			.then((result) => alive && setRows(result as ArchiveRow[]))
			.catch(() => alive && setRows(null));
		return () => {
			alive = false;
		};
	}, []);

	return rows ?? null;
}

/**
 * Плоская выборка → лес. Архивация — флаг НА МЕСТЕ, `parent_id` не трогается
 * (spec-tasks, Область 1), поэтому иерархия в выборке уже есть, её надо только собрать.
 *
 * Корень леса — архивная граница: нода, чей родитель в выборку не попал (живой, либо root).
 * Монотонность архива вниз гарантирует, что всё под границей — в выборке, дырок нет.
 * Корни — свежие сверху (что выкинул последним, то и ищешь); внутри ветки — порядок
 * аутлайна, каким он был до архивации.
 */
export function buildArchiveForest(rows: readonly ArchiveRow[]): ArchiveTreeNode[] {
	const byId = new Map<string, ArchiveTreeNode>();
	for (const row of rows) {
		byId.set(row.id, { row, children: [] });
	}
	const roots: ArchiveTreeNode[] = [];
	for (const node of byId.values()) {
		const parent = node.row.parentId ? byId.get(node.row.parentId) : undefined;
		if (parent) {
			parent.children.push(node);
		} else {
			roots.push(node);
		}
	}
	for (const node of byId.values()) {
		node.children.sort((a, b) => comparePositions(a.row, b.row));
	}
	return roots.sort((a, b) => (b.row.archivedAt?.getTime() ?? 0) - (a.row.archivedAt?.getTime() ?? 0));
}
